import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckIcon from '@mui/icons-material/Check';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { toast } from 'react-toastify';
import InvDataGrid from '../InvDataGrid';
import StatusChip from '../StatusChip';
import InvButton from '../InvButton';
import ConfirmDialog from '../ConfirmDialog';
import FilterToolbar from '../FilterToolbar';
import SectionCard from '../SectionCard';
import InvTabBar from '../InvTabBar';
import {
  getStockAdjustments,
  getPendingStockAdjustments,
  authorizeStockAdjustment,
  cancelStockAdjustment,
} from '../../api/stockAdjustment';
import { getStores } from '../../api/stockLedger';
import { toInventoryList } from '../../api/inventory-request';
import { STOCK_ADJUSTMENT_STATUSES } from '../../constants/storeManagement';
import { storeDisplayName } from '../../utils/storeEntity';
import { filterStoresByAssignment } from '../../utils/assignedStores';
import { formatDateTime } from '../../utils';
import useInvRole from '../../hooks/useInvRole';
import { INV_ACTIONS } from '../../utils/inventoryRoles';
import { IPD_COLORS } from '../../theme';

const LIST_TABS = [
  { id: 'pending', label: 'Pending' },
  { id: 'all', label: 'All' },
];

export default function StockAdjustmentListPanel() {
  const history = useHistory();
  const { can, myStoreId, myStoreIds } = useInvRole();
  const { url } = useRouteMatch();
  const base = url.replace(/\/adjustments.*/, '/adjustments');

  const [listTab, setListTab] = useState('pending');
  const [items, setItems] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // A store-scoped user (Store Keeper) only sees their own store, so seed the filter with
  // it rather than "All stores". myStoreId is settled on first render: InvRoleRoute holds
  // the spinner until permissions resolve.
  const storeLocked = myStoreIds.length === 1;
  const [storeId, setStoreId] = useState(storeLocked ? (myStoreId || '') : '');
  const [statusFilter, setStatusFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [acting, setActing] = useState(false);

  useEffect(() => {
    getStores()
      .then(d => setStores(toInventoryList(d)))
      .catch(() => {});
  }, []);

  const storeName = useCallback((id) => {
    if (!id) return '—';
    const s = stores.find(r => (r.store_id || r.id) === id);
    return s ? storeDisplayName(s) : id;
  }, [stores]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { limit: 100 };
      if (storeId) params.store_id = storeId;
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;

      let data;
      if (listTab === 'pending') {
        // API: GET /inventory/stock-adjustments/pending/
        data = await getPendingStockAdjustments(params);
      } else {
        if (statusFilter) params.status = statusFilter;
        // API: GET /inventory/stock-adjustments/
        data = await getStockAdjustments(params);
      }
      setItems(toInventoryList(data));
    } catch (e) {
      setError(e.message);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [listTab, storeId, statusFilter, fromDate, toDate]);

  useEffect(() => { load(); }, [load]);

  async function runConfirm() {
    if (!confirmTarget || !confirmAction) return;
    const required = confirmAction === 'authorize'
      ? INV_ACTIONS.ADJUSTMENT_AUTHORIZE
      : INV_ACTIONS.ADJUSTMENT_CANCEL;
    if (!can(required)) return;
    setActing(true);
    try {
      const id = confirmTarget.id;
      if (confirmAction === 'authorize') {
        // API: POST /inventory/stock-adjustments/{id}/authorize/
        await authorizeStockAdjustment(id);
        toast.success('Adjustment authorized — inventory updated');
      } else {
        // API: DELETE /inventory/stock-adjustments/{id}/
        await cancelStockAdjustment(id);
        toast.success('Adjustment cancelled');
      }
      setConfirmTarget(null);
      setConfirmAction(null);
      load();
    } catch (e) {
      toast.error(e.message || 'Action failed');
    } finally {
      setActing(false);
    }
  }

  const statusChipMinWidth = useMemo(() => {
    const labels = items.map(r => (r.status ? String(r.status).replace(/_/g, ' ') : '')).filter(Boolean);
    if (!labels.length) return undefined;
    return Math.ceil(Math.max(...labels.map(l => l.length)) * 7 + 20);
  }, [items]);

  const assignedStores = filterStoresByAssignment(stores, myStoreIds);
  const storeOptions = myStoreIds.length
    ? assignedStores.map(s => ({ value: s.store_id || s.id, label: storeDisplayName(s) }))
    : [
      { value: '', label: 'All stores' },
      ...stores.map(s => ({ value: s.store_id || s.id, label: storeDisplayName(s) })),
    ];

  // Empty until getStores() lands — better a blank than the bare UUID storeName() falls
  // back to.
  const lockedStoreLabel = storeOptions.find(o => o.value === myStoreId)?.label || '';

  const columns = useMemo(() => [
    { field: 'adjustment_number', headerName: 'Adjustment #', width: 170 },
    {
      field: 'store_id',
      headerName: 'Store',
      width: 180,
      valueFormatter: p => storeName(p.value),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 130,
      renderCell: p => <StatusChip status={p.value} minWidth={statusChipMinWidth} />,
    },
    {
      field: 'reason',
      headerName: 'Reason',
      flex: 1,
      minWidth: 160,
      valueFormatter: p => p.value || '—',
    },
    {
      field: 'lines',
      headerName: 'Lines',
      width: 80,
      valueGetter: p => Array.isArray(p.row.lines) ? p.row.lines.length : (p.row.line_count ?? '—'),
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 140,
      valueFormatter: p => formatDateTime(p.value),
    },
    {
      field: 'authorized_at',
      headerName: 'Authorized',
      width: 140,
      valueFormatter: p => formatDateTime(p.value),
    },
    {
      field: 'actions',
      headerName: '',
      width: 110,
      sortable: false,
      renderCell: p => (
        <Box sx={{ display: 'flex', gap: 0.25 }}>
          <Tooltip title="View">
            <IconButton
              size="small"
              onClick={() => history.push(`${base}/${p.row.id}`)}
              sx={{ color: IPD_COLORS.primary }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {p.row.status === 'DRAFT' && (
            <>
              {can(INV_ACTIONS.ADJUSTMENT_AUTHORIZE) && (
                <Tooltip title="Authorize">
                  <IconButton
                    size="small"
                    onClick={() => { setConfirmAction('authorize'); setConfirmTarget(p.row); }}
                    sx={{ color: IPD_COLORS.success.text }}
                  >
                    <CheckIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {can(INV_ACTIONS.ADJUSTMENT_CANCEL) && (
                <Tooltip title="Cancel">
                  <IconButton
                    size="small"
                    onClick={() => { setConfirmAction('cancel'); setConfirmTarget(p.row); }}
                    sx={{ color: IPD_COLORS.error.text }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </>
          )}
        </Box>
      ),
    },
  ], [base, history, statusChipMinWidth, storeName, can]);

  return (
    <Box>
      <SectionCard
        title="Stock Adjustments"
        subtitle="Draft quantity corrections await authorization before stock changes"
        actions={can(INV_ACTIONS.ADJUSTMENT_CREATE) ? (
          <InvButton size="small" startIcon={<AddIcon />} onClick={() => history.push(`${base.replace(/\/adjustments$/, '')}/adjustment/new`)}>
            New Adjustment
          </InvButton>
        ) : null}
      >
        <InvTabBar
          tabs={LIST_TABS}
          activeTab={listTab}
          onChange={setListTab}
          ariaLabel="Adjustment list filters"
          sx={{ mb: 1.5 }}
        />

        <FilterToolbar>
          {storeLocked ? (
            <FilterToolbar.StaticValue label="Store" width={220} value={lockedStoreLabel} />
          ) : (
            <FilterToolbar.Filter
              label="Store"
              width={220}
              value={storeId}
              onChange={e => setStoreId(e.target.value)}
              options={storeOptions}
            />
          )}
          {listTab === 'all' && (
            <FilterToolbar.Filter
              label="Status"
              width={150}
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              options={[
                { value: '', label: 'All statuses' },
                ...STOCK_ADJUSTMENT_STATUSES.map(s => ({ value: s, label: s })),
              ]}
            />
          )}
          <FilterToolbar.DateFilter
            label="From"
            value={fromDate}
            onChange={e => setFromDate(e.target.value)}
          />
          <FilterToolbar.DateFilter
            label="To"
            value={toDate}
            onChange={e => setToDate(e.target.value)}
          />
        </FilterToolbar>

        <Typography variant="caption" sx={{ color: IPD_COLORS.textSecondary, display: 'block', mb: 1 }}>
          {items.length} record{items.length === 1 ? '' : 's'}
        </Typography>

        <InvDataGrid
          rows={items}
          columns={columns}
          loading={loading}
          error={error}
          onRetry={load}
          getRowId={r => r.id}
          emptyMessage={listTab === 'pending' ? 'No pending adjustments.' : 'No adjustments found.'}
        />
      </SectionCard>

      <ConfirmDialog
        open={Boolean(confirmTarget)}
        title={confirmAction === 'authorize' ? 'Authorize Adjustment' : 'Cancel Adjustment'}
        description={
          confirmAction === 'authorize'
            ? `Authorize "${confirmTarget?.adjustment_number}"? Stock balances and ledger will update.`
            : `Cancel "${confirmTarget?.adjustment_number}"? This cannot be undone.`
        }
        destructive={confirmAction === 'cancel'}
        loading={acting}
        onConfirm={runConfirm}
        onCancel={() => { setConfirmTarget(null); setConfirmAction(null); }}
      />
    </Box>
  );
}
